import {
  type AdminLlmPromptTemplateSelectDto,
  type AdminLlmPromptTemplateVersionSelectDto,
  LlmModel,
  type LlmModelSelectDto,
} from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { UnsavedChangesModal } from '@components/shared/unsaved-changes-modal.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { errorToast, successToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { useLlmModelsQueryOptions } from '@queries/llm.query';
import {
  useAdminActivateLlmPromptTemplateVersionMutation,
  useAdminCreateLlmPromptTemplateVersionMutation,
  useAdminLlmPromptQuery,
} from '@queries/llm.query';
import {
  AdminLlmPromptTemplateFormData,
  buildAdminLlmPromptTemplateSchema,
} from '@schemas/admin-llm-prompt-template.schema';
import { useStore } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFormInvalidHandler } from '@utils/form.util';
import { isStructuredOutputModelSelectable } from '@utils/llm-model.util';
import { mapToSelectOptions } from '@utils/select.util';
import { CheckIcon, ChevronRightIcon, CopyIcon, SaveIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildPromptTemplateEditorChangeState,
  buildPromptTemplatePlaceholder,
  formatPromptCategoryLabel,
  formatPromptListTitle,
  insertPromptTemplatePlaceholderAtSelection,
  validatePromptTemplatePlaceholders,
} from '../admin-llm-prompts.util';

type AdminLlmPromptModalProps = {
  initialData?: AdminLlmPromptTemplateSelectDto;
  isOpen: boolean;
  onClose: () => void;
  promptKey: string;
};

type PendingAction =
  | { type: 'close' }
  | {
      type: 'switch-version';
      version: number;
    };

function buildDefaultValues(prompt?: AdminLlmPromptTemplateSelectDto): AdminLlmPromptTemplateFormData {
  return {
    defaultModel: prompt?.defaultModel ?? prompt?.resolvedModel ?? LlmModel.OpenrouterAuto,
    template: prompt?.template ?? '',
  };
}

function buildModelOptions(models: LlmModelSelectDto[]) {
  return mapToSelectOptions(models.filter(isStructuredOutputModelSelectable));
}

function buildVersionLabel(version: number): string {
  return `Version ${version}`;
}

function buildHeaderVersionLabel(version: number): string {
  return `v${version}`;
}

function buildVersionOptions(
  versions: AdminLlmPromptTemplateVersionSelectDto[],
): AdminLlmPromptTemplateVersionSelectDto[] {
  return versions.slice().sort((left, right) => left.version - right.version);
}

function dispatchTextareaInputEvent(textarea: HTMLTextAreaElement): void {
  textarea.dispatchEvent(new Event('input', { bubbles: true }));
}

function insertTextareaTextWithNativeUndo(params: {
  selectionEnd: number;
  selectionStart: number;
  text: string;
  textarea: HTMLTextAreaElement;
}): boolean {
  const { selectionEnd, selectionStart, text, textarea } = params;
  const normalizedSelectionEnd = Math.max(selectionEnd, selectionStart);
  const normalizedSelectionStart = Math.min(selectionEnd, selectionStart);

  textarea.focus({ preventScroll: true });
  textarea.setSelectionRange(normalizedSelectionStart, normalizedSelectionEnd);

  if (typeof document.execCommand !== 'function') {
    return false;
  }

  try {
    return document.execCommand('insertText', false, text);
  } catch {
    return false;
  }
}

export const AdminLlmPromptModal: React.FC<AdminLlmPromptModalProps> = (props) => {
  const { initialData, isOpen, onClose, promptKey } = props;
  const [selectedVersion, setSelectedVersion] = useState<number | undefined>(
    initialData?.activeVersion ?? initialData?.version,
  );
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const templateTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const templateSelectionRef = useRef({ selectionEnd: 0, selectionStart: 0 });

  const llmModelsQueryOptions = useLlmModelsQueryOptions();
  const { data: availableModels = [] } = useQuery(llmModelsQueryOptions);
  const { data: prompt, isPending } = useAdminLlmPromptQuery({
    editing: isOpen,
    initialData,
    key: promptKey,
    version: selectedVersion,
  });
  const { mutateAsync: activatePromptTemplateVersion, isPending: isActivating } =
    useAdminActivateLlmPromptTemplateVersionMutation();
  const { mutateAsync: createPromptTemplateVersion, isPending: isSaving } =
    useAdminCreateLlmPromptTemplateVersionMutation();

  const defaultValues = useMemo(() => buildDefaultValues(prompt), [prompt]);
  const modelOptions = useMemo(() => buildModelOptions(availableModels), [availableModels]);
  const supportsDefaultModelOverride =
    prompt?.supportsDefaultModelOverride ?? initialData?.supportsDefaultModelOverride ?? false;
  const validationSchema = useMemo(
    () =>
      buildAdminLlmPromptTemplateSchema({
        supportsDefaultModelOverride,
      }),
    [supportsDefaultModelOverride],
  );
  const versionOptions = useMemo(() => buildVersionOptions(prompt?.versions ?? []), [prompt?.versions]);
  const selectedVersionOption = useMemo(
    () => versionOptions.find((version) => version.version === selectedVersion),
    [selectedVersion, versionOptions],
  );

  const savePromptVersion = useCallback(
    async (value: AdminLlmPromptTemplateFormData): Promise<boolean> => {
      if (!prompt) {
        return false;
      }

      const placeholderValidation = validatePromptTemplatePlaceholders({
        allowedVariables: prompt.variables.map((variable) => variable.key),
        template: value.template,
      });

      if (placeholderValidation.unknownPlaceholders.length > 0) {
        errorToast({
          description: `Unknown placeholders: ${placeholderValidation.unknownPlaceholders.join(', ')}`,
        });
        return false;
      }

      try {
        const nextPrompt = await createPromptTemplateVersion({
          dto: {
            baseVersion: prompt.version,
            defaultModel: prompt.supportsDefaultModelOverride ? value.defaultModel : undefined,
            template: value.template,
          },
          key: prompt.key,
        });

        setSelectedVersion(nextPrompt.version);
        successToast({
          description:
            nextPrompt.version === prompt.version
              ? 'Saved prompt settings.'
              : `Saved ${buildVersionLabel(nextPrompt.version)}.`,
        });
        return true;
      } catch (error) {
        if (error instanceof Error) {
          errorToast({ description: error.message });
          return false;
        }

        errorToast({ description: 'Failed to update prompt template.' });
        return false;
      }
    },
    [createPromptTemplateVersion, prompt],
  );

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      await savePromptVersion(value);
    },
    onSubmitInvalid: createFormInvalidHandler(),
    validators: {
      onSubmit: validationSchema,
    },
  });

  const canSubmit = useStore(form.store, (state) => state.canSubmit);

  const formValues = useStore(form.store, (state) => state.values);
  const templateValue = useStore(form.store, (state) => state.values.template ?? '');
  const { hasUnsavedChanges, isCreatingNewVersion } = useMemo(() => {
    return buildPromptTemplateEditorChangeState({
      formData: formValues,
      prompt,
    });
  }, [formValues, prompt]);
  const placeholderValidation = useMemo(() => {
    return validatePromptTemplatePlaceholders({
      allowedVariables: prompt?.variables.map((variable) => variable.key) ?? [],
      template: templateValue,
    });
  }, [prompt?.variables, templateValue]);

  useEffect(() => {
    if (!isOpen || !prompt) {
      return;
    }

    form.reset(buildDefaultValues(prompt));
  }, [form, isOpen, prompt]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelectedVersion(initialData?.activeVersion ?? initialData?.version);
    setIsUnsavedChangesModalOpen(false);
    setPendingAction(null);
  }, [initialData?.activeVersion, initialData?.version, isOpen]);

  const handleActivateVersion = useCallback(async (): Promise<void> => {
    if (!prompt || selectedVersion === undefined || selectedVersion === prompt.activeVersion) {
      return;
    }

    try {
      const nextPrompt = await activatePromptTemplateVersion({
        dto: {
          version: selectedVersion,
        },
        key: prompt.key,
      });

      setSelectedVersion(nextPrompt.activeVersion);
      successToast({ description: `Activated ${buildVersionLabel(nextPrompt.activeVersion)}.` });
    } catch (error) {
      if (error instanceof Error) {
        errorToast({ description: error.message });
        return;
      }

      errorToast({ description: 'Failed to activate prompt template version.' });
    }
  }, [activatePromptTemplateVersion, prompt, selectedVersion]);

  const performPendingAction = useCallback(
    (action: PendingAction | null): void => {
      if (!action) {
        return;
      }

      if (action.type === 'close') {
        form.reset(defaultValues);
        onClose();
        return;
      }

      setSelectedVersion(action.version);
    },
    [defaultValues, form, onClose],
  );

  const handleVersionSelect = useCallback(
    (nextVersion: number): void => {
      if (nextVersion === selectedVersion) {
        return;
      }

      if (hasUnsavedChanges) {
        setPendingAction({
          type: 'switch-version',
          version: nextVersion,
        });
        setIsUnsavedChangesModalOpen(true);
        return;
      }

      setSelectedVersion(nextVersion);
    },
    [hasUnsavedChanges, selectedVersion],
  );

  const handleCloseRequest = useCallback((): void => {
    if (hasUnsavedChanges) {
      setPendingAction({ type: 'close' });
      setIsUnsavedChangesModalOpen(true);
      return;
    }

    form.reset(defaultValues);
    onClose();
  }, [defaultValues, form, hasUnsavedChanges, onClose]);

  const handleDiscardPendingChanges = useCallback((): void => {
    const nextAction = pendingAction;

    setIsUnsavedChangesModalOpen(false);
    setPendingAction(null);
    performPendingAction(nextAction);
  }, [pendingAction, performPendingAction]);

  const handleCancelPendingChanges = useCallback((): void => {
    setIsUnsavedChangesModalOpen(false);
    setPendingAction(null);
  }, []);

  const handleSaveAndContinue = useCallback(async (): Promise<void> => {
    const nextAction = pendingAction;

    if (!nextAction) {
      return;
    }
    const didSave = await savePromptVersion(formValues);

    if (!didSave) {
      return;
    }

    setIsUnsavedChangesModalOpen(false);
    setPendingAction(null);
    performPendingAction(nextAction);
  }, [formValues, pendingAction, performPendingAction, savePromptVersion]);

  const syncTemplateSelection = useCallback((textarea?: HTMLTextAreaElement | null): void => {
    if (!textarea) {
      return;
    }

    const fallbackPosition = textarea.value.length;

    templateSelectionRef.current = {
      selectionEnd: textarea.selectionEnd ?? fallbackPosition,
      selectionStart: textarea.selectionStart ?? fallbackPosition,
    };
  }, []);

  const handleInsertPlaceholder = useCallback(
    (key: string): void => {
      const nextPlaceholder = buildPromptTemplatePlaceholder(key);
      const textarea = templateTextareaRef.current;

      if (!textarea) {
        const { nextTemplate } = insertPromptTemplatePlaceholderAtSelection({
          placeholder: nextPlaceholder,
          selectionEnd: templateSelectionRef.current.selectionEnd,
          selectionStart: templateSelectionRef.current.selectionStart,
          template: templateValue,
        });

        form.setFieldValue('template', nextTemplate);
        return;
      }

      const selectionEnd = textarea.selectionEnd ?? templateSelectionRef.current.selectionEnd;
      const selectionStart = textarea.selectionStart ?? templateSelectionRef.current.selectionStart;

      const { nextCursorPosition, nextTemplate } = insertPromptTemplatePlaceholderAtSelection({
        placeholder: nextPlaceholder,
        selectionEnd,
        selectionStart,
        template: textarea.value,
      });

      const didInsertWithNativeUndo = insertTextareaTextWithNativeUndo({
        selectionEnd,
        selectionStart,
        text: nextPlaceholder,
        textarea,
      });

      if (didInsertWithNativeUndo) {
        dispatchTextareaInputEvent(textarea);
      } else {
        textarea.setRangeText(nextPlaceholder, selectionStart, selectionEnd, 'end');
        dispatchTextareaInputEvent(textarea);
      }

      requestAnimationFrame(() => {
        if (textarea.value !== nextTemplate) {
          textarea.value = nextTemplate;
          dispatchTextareaInputEvent(textarea);
        }

        textarea.focus({ preventScroll: true });
        textarea.setSelectionRange(nextCursorPosition, nextCursorPosition);
        syncTemplateSelection(textarea);
      });
    },
    [form, syncTemplateSelection, templateValue],
  );

  const handleClose = useCallback((): void => {
    handleCloseRequest();
  }, [handleCloseRequest]);

  if (!isOpen) {
    return null;
  }

  const isActivateVisible =
    !hasUnsavedChanges && selectedVersion !== undefined && selectedVersion !== prompt?.activeVersion;
  const isSaveDisabled = isPending || isSaving || !canSubmit || !hasUnsavedChanges;
  const isVersionSelectDisabled = isPending || isSaving || isActivating;
  const isPendingActionSaving = isSaving;
  const hasVariables = (prompt?.variables.length ?? 0) > 0;
  const hasSidebar = supportsDefaultModelOverride || hasVariables;

  return (
    <>
      <Modal
        centered
        isOpen={isOpen}
        onClose={handleClose}
        header={{
          className: 'text-left',
          subtitle: prompt ? (
            <p className="text-sm text-gray-500">{formatPromptCategoryLabel(prompt.category)}</p>
          ) : null,
          title: (
            <div className="flex items-center gap-2">
              <span>{prompt ? formatPromptListTitle(prompt) : 'Prompt Template'}</span>
              {prompt && (
                <Badge
                  size="xs"
                  variant="primary"
                >
                  {buildHeaderVersionLabel(prompt.activeVersion)}
                </Badge>
              )}
            </div>
          ),
        }}
        footer={{
          leftButton: (
            <div className="flex items-center gap-3">
              <Select
                disabled={isVersionSelectDisabled}
                value={selectedVersion?.toString()}
                onValueChange={(value) => {
                  handleVersionSelect(Number(value));
                }}
              >
                <SelectTrigger className="w-[180px] bg-gray-100">
                  {selectedVersionOption ? (
                    <div className="flex items-center gap-2 truncate">
                      <span className="truncate">{buildVersionLabel(selectedVersionOption.version)}</span>
                      {selectedVersionOption.isActive && (
                        <Badge
                          size="xs"
                          variant="primary"
                        >
                          Active
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <SelectValue placeholder="Version" />
                  )}
                </SelectTrigger>
                <SelectContent
                  avoidCollisions={false}
                  side="top"
                >
                  {versionOptions.map((option) => (
                    <SelectItem
                      key={option.version}
                      textValue={`${buildVersionLabel(option.version)}${option.isActive ? ' Active' : ''}`}
                      value={option.version.toString()}
                    >
                      <div className="flex items-center gap-2">
                        <span>{buildVersionLabel(option.version)}</span>
                        {option.isActive && (
                          <Badge
                            size="xs"
                            variant="primary"
                          >
                            Active
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {isActivateVisible && (
                <Button
                  className="min-w-[132px] justify-start"
                  disabled={isPending || isSaving || isActivating}
                  onClick={() => {
                    void handleActivateVersion();
                  }}
                  variant="ghost"
                >
                  Activate this Version
                </Button>
              )}
            </div>
          ),
          rightButton: (
            <div className="flex min-h-9 min-w-[170px] justify-end">
              {hasUnsavedChanges ? (
                <Button
                  className="gap-2"
                  disabled={isSaveDisabled}
                  size="sm"
                  onClick={() => {
                    void form.handleSubmit();
                  }}
                >
                  <SaveIcon className="size-3.5" />
                  {isCreatingNewVersion ? 'Save New Version' : 'Save Changes'}
                </Button>
              ) : (
                <div className="h-8 w-[170px]" />
              )}
            </div>
          ),
        }}
        contentClassname="flex flex-col"
        height="calc(92vh - var(--impersonation-banner-height, 0px))"
        width="980px"
      >
        {prompt ? (
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.AppForm>
              <div className="flex min-h-0 flex-1 flex-col gap-4">
                <div
                  className={cn(
                    'grid min-h-0 flex-1 gap-4',
                    hasSidebar ? 'grid-cols-[minmax(0,1fr)_280px]' : 'grid-cols-[minmax(0,1fr)]',
                  )}
                >
                  <div className="flex min-h-0 flex-col gap-3">
                    <form.AppField name="template">
                      {(field) => (
                        <field.TextAreaField
                          className="flex min-h-0 flex-1 flex-col"
                          label="Template"
                          onClick={(event) => {
                            syncTemplateSelection(event.currentTarget);
                          }}
                          onKeyUp={(event) => {
                            syncTemplateSelection(event.currentTarget);
                          }}
                          onSelect={(event) => {
                            syncTemplateSelection(event.currentTarget);
                          }}
                          placeholder="Write the prompt template"
                          ref={templateTextareaRef}
                          rows={1}
                          textareaClassName="h-full min-h-0 resize-none overflow-y-auto font-mono text-xs leading-relaxed"
                          textareaContainerClassName="flex min-h-0 flex-1"
                          tooltip="Content inside {{}} is treated as a variable and replaced at runtime."
                        />
                      )}
                    </form.AppField>

                    {placeholderValidation.unknownPlaceholders.length > 0 && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                        Unknown placeholders: {placeholderValidation.unknownPlaceholders.join(', ')}
                      </div>
                    )}
                  </div>

                  {hasSidebar && (
                    <div className="flex min-h-0 flex-col gap-4">
                      {supportsDefaultModelOverride && (
                        <form.AppField name="defaultModel">
                          {(field) => (
                            <field.SelectField
                              label="Default model"
                              options={modelOptions}
                              placeholder="Select a default model"
                            />
                          )}
                        </form.AppField>
                      )}

                      {hasVariables && (
                        <div className="flex min-h-0 flex-1 flex-col gap-2">
                          <div className="flex items-center gap-1 text-sm font-medium text-gray-800">
                            Variables
                            <TooltipComponent>
                              These placeholders are replaced with actual values at runtime when the prompt is sent to
                              the LLM. Click a variable to insert it at the cursor.
                            </TooltipComponent>
                          </div>

                          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
                            {prompt.variables.map((variable) => (
                              <VariableSidebarItem
                                key={variable.key}
                                description={variable.description}
                                example={variable.example}
                                isUsed={placeholderValidation.usedVariables.includes(variable.key)}
                                onInsert={handleInsertPlaceholder}
                                variableKey={variable.key}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </form.AppForm>
          </form>
        ) : (
          <div className="py-10 text-center text-xs text-gray-500">Loading prompt template...</div>
        )}
      </Modal>

      <UnsavedChangesModal
        isOpen={isUnsavedChangesModalOpen}
        isSaving={isPendingActionSaving}
        onCancel={handleCancelPendingChanges}
        onDiscard={handleDiscardPendingChanges}
        onSaveAndContinue={() => {
          void handleSaveAndContinue();
        }}
      />
    </>
  );
};

type VariableSidebarItemProps = {
  description: string;
  example: string;
  isUsed: boolean;
  onInsert: (key: string) => void;
  variableKey: string;
};

const VariableSidebarItem: React.FC<VariableSidebarItemProps> = (props) => {
  const { description, example, isUsed, onInsert, variableKey } = props;
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const placeholder = buildPromptTemplatePlaceholder(variableKey);

  useEffect(() => {
    return (): void => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback((): void => {
    void navigator.clipboard.writeText(placeholder);
    setCopied(true);

    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }

    copyTimeoutRef.current = setTimeout(() => {
      setCopied(false);
    }, 1500);
  }, [placeholder]);

  return (
    <div
      className={cn(
        'rounded-lg border p-2.5 transition-colors',
        isUsed ? 'border-blue-200 bg-blue-50/70' : 'border-gray-200 bg-white',
      )}
    >
      <div className="flex items-start gap-2">
        <TooltipComponent
          trigger={
            <button
              className={cn(
                'min-w-0 flex-1 cursor-pointer rounded-md border px-2 py-1.5 text-left transition-colors',
                isUsed
                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200 hover:bg-blue-50',
              )}
              onClick={() => {
                onInsert(variableKey);
              }}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              type="button"
            >
              <span className="block min-w-0 truncate font-mono text-[11px]">{placeholder}</span>
            </button>
          }
        >
          Add to template
        </TooltipComponent>

        <TooltipComponent
          trigger={
            <button
              className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 transition-colors hover:bg-gray-50"
              onClick={handleCopy}
              type="button"
            >
              {copied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />}
            </button>
          }
        >
          Copy variable
        </TooltipComponent>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <button
          className="flex w-fit cursor-pointer items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
          onClick={() => {
            setIsExpanded((currentValue) => !currentValue);
          }}
          type="button"
        >
          <ChevronRightIcon className={cn('size-3 transition-transform', isExpanded && 'rotate-90')} />
          Details
        </button>

        {isUsed && (
          <span className="flex shrink-0 items-center gap-0.5 self-end text-[10px] text-blue-600">
            <CheckIcon className="size-3" />
            Used
          </span>
        )}
      </div>

      {isExpanded && (
        <div className="mt-1.5 space-y-1.5">
          <p className="text-[11px] text-gray-500">{description}</p>
          {example && (
            <pre className="overflow-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-white p-2 font-mono text-[11px] leading-relaxed text-gray-600">
              {example}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};
